import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { AuctionDownloadedService } from '@core/services/auction-downloaded.service';
import { AuthService } from '@core/services/auth.service';
import { ModalUtilsService } from '@core/services/modal-utils.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faBell,
  faEnvelope,
  faHeart,
  faUser,
} from '@fortawesome/free-regular-svg-icons';
import {
  faCartPlus,
  faChevronDown,
  faSearch,
  faShoppingBag,
  faSignOutAlt,
  faSlidersH,
} from '@fortawesome/free-solid-svg-icons';
import {
  NgbCollapseModule,
  NgbDropdownModule,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { ILanguage } from '@shared/interfaces/language.interface';
import { Catalog, FilterStatus } from '@shared/interfaces/search.interface';
import { LanguageService } from '@shared/services/language.service';
import { MessageService } from '@shared/services/message.service';
import { RedirectService } from '@shared/services/redirect.service';
import { SearchService } from '@shared/services/search.service';
import { NotificationService } from 'src/app/account/services/notification.service';
import { ContactsDialogComponent } from 'src/app/article/components/dialogs/contacts-dialog/contacts-dialog.component';
import { AccountDialogComponent } from 'src/app/login/components/account-dialog/account-dialog.component';
import { ActivateAccountPopupComponent } from 'src/app/login/pages/activate-account-popup/activate-account-popup.component';
import { LoginService } from 'src/app/login/services/login.service';
import { PagesService } from 'src/app/pages-manager/services/pages.service';
import { SellerBrandingService } from 'src/app/seller/services/seller-branding.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  // TODO: this must be fixed properly
  host: { ngSkipHydration: 'true' },
  imports: [
    CommonModule,
    RouterModule,
    FaIconComponent,
    NgbDropdownModule,
    TranslatePipe,
    FormsModule,
    NgbCollapseModule,
  ],
})
export class HeaderComponent {
  faHeart = faHeart;
  faBell = faBell;
  faEnvelope = faEnvelope;
  faUser = faUser;
  faSearch = faSearch;
  faSlidersH = faSlidersH;
  faChevronDown = faChevronDown;
  faSignOutAlt = faSignOutAlt;
  faCartPlus = faCartPlus;
  faShoppingBag = faShoppingBag;

  public isMenuCollapsed = true;

  isProd = !environment.production;

  isAutonomousSeller = computed(() => {
    return (
      this.authService.isAuthenticated() &&
      this.authService.userLogged.idProfile === 3 &&
      this.authService.userLogged.isAutonomousSeller
    );
  });

  imgCountry: string = '/assets/img/it.png';

  @ViewChild('searchInputHeader') searchInputHeader: ElementRef;
  @ViewChild('accountDropdown') accountDropdown;

  selectedIdAuctionType = -1;
  selectedStringAuctionType = 'IDS_ALL';
  searchText: string = '';
  @Input() hideSearch: boolean = true;

  isShopPage = false;

  links = signal([
    {
      label: 'IDS_ASTE',
      route: '/search-result-all',
      isActivated: false,
    },
    {
      label: 'IDS_OTHER_CATEGORIES',
      route: '/prestige',
      isActivated: false,
    },
    {
      label: 'IDS_SHOP',
      route: '/shop',
      isActivated: false,
    },
  ] as {
    label: string;
    route: string;
    isActivated: boolean;
  }[]);

  linksRight = [
    {
      label: 'IDS_WHO_WE_ARE',
      route: '/about-us',
      class: '',
    },
    {
      label: 'IDS_HOW_WORK',
      route: '/how-works-memorabid',
      class: '',
    },
    {
      label: 'IDS_BLOG',
      route: '/blog',
      class: '',
    },
    {
      label: 'IDS_BECOME_SELLER',
      route: '/become-seller',
      class: 'become-seller-link',
    },
  ] satisfies {
    label: string;
    route: string;
    class: string;
  }[];

  authService = inject(AuthService);
  public readonly searchService = inject(SearchService);
  private readonly loginService = inject(LoginService);
  private readonly modalUtils = inject(ModalUtilsService);
  private readonly router = inject(Router);
  private readonly redirectService = inject(RedirectService);
  public readonly notificationService = inject(NotificationService);
  public readonly languageService = inject(LanguageService);
  private readonly auctionDownloadedService = inject(AuctionDownloadedService);
  public readonly messageService = inject(MessageService);
  private readonly branding = inject(SellerBrandingService);
  private readonly pagesService = inject(PagesService);
  headerPages = signal<{ label: string; route: string }[]>([]);
  private lastLoadedSellerId: number | null = null;

  homePath = computed(() => {
    if (this.isBrandedView()) {
      const brandUrl = this.branding.getBrandHomeUrl();
      if (brandUrl) {
        return `/${this.languageService.userSelectedLanguage.code}/${brandUrl}`;
      }
    }
    return `/${this.languageService.userSelectedLanguage.code}/home`;
  });

  constructor() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isShopPage = event.url.includes('shop');

        this.links.set(
          this.links().map((link) => ({
            ...link,
            isActivated: event.url.includes(link.route),
          }))
        );
      }
    });

    this.languageService.selectedLanguage$.subscribe((lang) => {
      if (lang) {
        this.setFlag(lang.code);
        this.loadHeaderPages();
      }
    });

    // React to branding activation to reload seller-specific header pages once seller id known
    effect(
      () => {
        const activeSeller = this.branding.activeSeller();
        if (this.branding.isBranded() && activeSeller?.idEntity) {
          this.loadHeaderPages();
        }
      },
      { allowSignalWrites: true }
    );
  }

  isBrandedView = computed(() => {
    return this.branding.isBranded();
  });

  // Only consider branded when branding decision resolved (avoids SSR hydration mismatch)
  showBranded = computed(
    () => this.branding.brandingReady() && this.branding.isBranded()
  );

  brandedLinks = signal([
    {
      label: 'IDS_HOW_WORK',
      route: '/how-works-memorabid',
      isActivated: false,
    },
    {
      label: 'IDS_WHO_WE_ARE',
      route: '/about-us',
      isActivated: false,
    },
  ]);

  private loadHeaderPages() {
    const activeSeller = this.branding.activeSeller();
    const idSeller = activeSeller?.idEntity;
    if (idSeller === this.lastLoadedSellerId || !idSeller) {
      return; // avoid unnecessary reload
    }
    this.pagesService.getHeaderPages(idSeller).subscribe((pages) => {
      this.brandedLinks.set([
        {
          label: 'IDS_SELLER_HOME',
          route: `/seller/${idSeller}`,
          isActivated: false,
        },
        ...pages.map((p) => ({
          label: p.pageName,
          route: '/' + (p.pageRoute || ''),
          isActivated: false,
        })),
      ]);
      this.lastLoadedSellerId = idSeller ?? null;
    });
  }

  trackByRoute(_index: number, item: { route: string }) {
    return item.route;
  }

  setFlag(lang: string) {
    this.imgCountry = '/assets/img/' + lang + '.png';
  }

  goToHome() {
    this.isMenuCollapsed = true;
    const brandUrl = this.branding.getBrandHomeUrl();
    if (this.isBrandedView() && brandUrl) {
      // Navigate to seller brand page (language preserved)
      this.router.navigate([
        '/',
        this.languageService.userSelectedLanguage.code,
        brandUrl.replace(/^\//, ''),
      ]);
    } else {
      this.redirectService.redirect('home');
    }
  }

  setAuctionType(id: number) {
    this.selectedIdAuctionType = id;
    const selectedAuctionType = this.searchService.auctionTypeList.find(
      (item) => item.key === id
    );
    this.selectedStringAuctionType = selectedAuctionType.value;
  }

  search() {
    this.auctionDownloadedService.scrollPosition = 0;
    this.searchInputHeader.nativeElement.blur();

    const auctionStatus = FilterStatus.All;
    const searchRoute = 'search-result';

    this.searchService.setFilters(searchRoute, {
      idLanguage: this.languageService.userSelectedLanguage.idLanguage,
      searchText: this.searchText,
      idAuctionType: -1,
      idDetail: null,
      onlySigned: false,
      idSport: null,
      idCompetition: null,
      idTeam: null,
      onlyNew: false,
      onlyExpiring: false,
      onlyArriving: false,
      onlyBuyNow: false,
      auctionStatus: auctionStatus,
      idEntity: null,
      idCampaign: null,
      idPromoter: null,
      idActiveAuction: null,
      idTagList: [],
      catalog: Catalog.All,
      orderBy: 'evidence',
      orderDirection: 'desc',
    });

    this.router.navigate(
      ['/', this.languageService.userSelectedLanguage.code, searchRoute],
      {
        queryParamsHandling: 'merge',
        replaceUrl: true,
      }
    );
    setTimeout(() => {
      this.searchService.updateQueryParams(this.searchService.filters);
    }, 500);
  }

  onIconPressed(
    nextPageRoute: string,
    _queryParams: {
      [k: string]: string | number | boolean | null | undefined;
    } = {}
  ) {
    if (this.authService.isAuthenticated()) {
      if (
        nextPageRoute === 'account' &&
        this.authService.userLogged.idProfile === 1
      ) {
        nextPageRoute = 'admin';
      }
      if (nextPageRoute === 'admin-v2') {
        window.open(environment.adminPath, '_blank');
        return;
      }
      this.redirectService.redirect(nextPageRoute);
    } else if (nextPageRoute === 'account/messages') {
      const modal = this.modalUtils.openModal(ContactsDialogComponent, {
        size: 'lg',
        keyboard: false,
      });
      modal.componentInstance.onClickIconMessageHeader = '/about-us';
    } else if (this.authService.authResult === -2) {
      this.modalUtils.openModal(ActivateAccountPopupComponent, {
        size: 'md',
        keyboard: false,
      });
    } else {
      const ref = this.modalUtils.openModal(AccountDialogComponent, {
        size: 'md',
        keyboard: false,
      });
      ref.componentInstance.nextPageRoute = nextPageRoute;
    }
  }

  onAccountPressed(nextPageRoute: string) {
    if (!this.authService.isAuthenticated()) {
      this.accountDropdown.close();
      if (this.authService.authResult === -2) {
        this.modalUtils.openModal(ActivateAccountPopupComponent, {
          size: 'md',
          keyboard: false,
        });
      } else {
        const ref = this.modalUtils.openModal(AccountDialogComponent, {
          size: 'md',
          keyboard: false,
        });
        ref.componentInstance.nextPageRoute = nextPageRoute;
      }
    }
  }

  logout() {
    if (this.authService.isAuthenticated() || this.authService.isToActivate()) {
      this.loginService.logout().subscribe(() => {
        this.authService.logout();
        this.notificationService.unreadNotificationsCount = 0;
        this.messageService.unreadMessagesCount = 0;
        this.messageService.clearRefreshTimer();
        this.redirectService.redirect('home');
      });
    }
  }

  changeLanguages(lang: ILanguage) {
    this.languageService.changeLanguage(lang);
  }
}
